import get from 'lodash.get';
import sortBy from 'lodash.sortby';

import { isPointBelowLine } from '@amplify/mini-aimline';
import { PERIOD } from '../../constants/enrollment';
import {
    MEASURE_SCORE_BY_RECOMMENDATION_MEASURE,
    RECOMMENDATION_STATUSES,
} from '../../constants/assessments';

import {
    getAimlinePointsGroupedByMeasure,
    getAimlineStartPoint,
    getAimlineMiddlePoints,
    getAimlineEndPoint,
    getD8MeasureAimlineThreshold,
    getLecturaMeasureAimlineThreshold,
} from '../utils/aimlineHelpers';

import { parseDate } from '../utils/assessmentWindowsUtils';
import { DIBELS8, IDEL2 } from '../../constants/applications';

const getMeasureAimlineThreshold = {
    [DIBELS8]: getD8MeasureAimlineThreshold,
    [IDEL2]: getLecturaMeasureAimlineThreshold,
};

/**
 * Gets latest result for selected measure
 * @param {Array<Object>} results for period
 * @param {String} measure we need to filter results by
 * @returns {Object} latest result
 */
const getLatestMeasureResult = (results, measure) => {
    let latestResult = null;

    const measureResults = results.filter(result => result.measure === measure);

    if (measureResults.length) {
        // sort results by date and slice the last one
        [latestResult] = sortBy(measureResults, result => parseDate(result.clientModDate)).slice(
            -1,
        );
    }

    return latestResult;
};

/**
 * Calculate amount of aimline points which are below aimline
 *
 * @param {Object} aimlineStartPoint start point for MiniAimline library in the format {<date>: {Date}, <score>: {Number}}
 * @param {Array<Object>} aimlineMiddlePoints array with middle points for MiniAimline library
 * @param {Object} aimlineEndPoint end point for MiniAimline library
 * @returns {Number} amount of PM results which are below aimline
 */
const getPointsBelowAimlineAmount = (aimlineStartPoint, aimlineMiddlePoints, aimlineEndPoint) => {
    // find all points which are below aimline
    const aimlineBelowPoints = aimlineMiddlePoints.filter(aimlineMiddlePoint =>
        isPointBelowLine(aimlineMiddlePoint, aimlineStartPoint, aimlineEndPoint),
    );

    // and return amount of points we found
    return aimlineBelowPoints.length;
};

/**
 * Get recommendation statuses (on/off track) based on PM recommended measures
 *
 * @param {Object} student student data with Lectura app statuses including
 *                         interp and gating status
 * @param {Object} eligibleResults contains all eligible student results grouped by period
 * @param {Array<String>} appRecommendedMeasures ['PSF', 'NWF] or [] if nothing to recommend
 * @param {String} currentBMPeriod benchmark period which current date falls into
 * @param {Object} nextGoalPeriod object with information about next goal period (MOY or EOY)
 * @param {Object} cutscores grade/period/measure map of cutscores
 * @param {Object} studentsGoals object with teacher set goals for all students mapped by
 *                  student/grade/period
 * @param {Object} studentsZoG zones of growth mapped by student id
 * @param {String} appName name of the for which we are calculating for (DIBELS8, IDEL2)
 * @returns {Object} map, recommendation statuses grouped by measure:
                    {<measure> : <recommendation status>}
 */
export const getRecommendationStatusesPerApplication = (
    appName,
    student,
    eligibleResults,
    appRecommendedMeasures,
    currentBMPeriod,
    nextGoalPeriod,
    cutscores,
    studentsGoals,
    studentsZoG,
) => {
    const recommendationStatuses = {};

    // no next goal period, nothing to do
    if (!nextGoalPeriod || !appRecommendedMeasures.length) {
        return recommendationStatuses;
    }

    const nextPeriodCutscores = get(cutscores, [student.grade, nextGoalPeriod.period], {});

    const studentNextPeriodGoals = get(
        studentsGoals,
        [student.id, student.grade, nextGoalPeriod.period],
        {},
    );

    const studentZoG = get(studentsZoG, student.id, null)?.filter(
        zog => zog.gradeLevel === student.grade,
    );

    // group aimline points grouped by measure
    const aimlinePointsByMeasure = getAimlinePointsGroupedByMeasure(eligibleResults[PERIOD.PM]);

    // go through measures with aimline rule
    appRecommendedMeasures.forEach(measure => {
        const aimlineMeasurePoints = aimlinePointsByMeasure[measure];
        // get measure score key
        const measureScoreKey = MEASURE_SCORE_BY_RECOMMENDATION_MEASURE[appName][measure];
        // get aimline threshold for this measure
        const measureAimlineThreshold = getMeasureAimlineThreshold[appName](measure);
        // get latest BM result
        const latestBMResult = getLatestMeasureResult(eligibleResults[currentBMPeriod], measure);
        // so, if there is latest BM result and we have enough amount of aimline points,
        // proceed with aimline rule processing for this measure

        if (
            latestBMResult &&
            aimlineMeasurePoints &&
            aimlineMeasurePoints.length >= measureAimlineThreshold
        ) {
            const aimlineStartPoint = getAimlineStartPoint(latestBMResult);

            const measureCutscore = nextPeriodCutscores[measureScoreKey];
            const measureGoal = studentNextPeriodGoals[measureScoreKey];
            const aimlineEndPoint = getAimlineEndPoint(
                measureScoreKey,
                nextGoalPeriod,
                latestBMResult,
                measureGoal,
                measureCutscore,
                studentZoG,
                appName,
            );
            //  if no aimline points, skip this measure
            if (!aimlineStartPoint || !aimlineEndPoint) {
                return;
            }
            const aimlineMiddlePoints = getAimlineMiddlePoints(
                aimlineStartPoint,
                aimlineMeasurePoints,
                aimlineEndPoint,
                measureAimlineThreshold,
            );

            // if we have enough of middle points
            if (aimlineMiddlePoints.length === measureAimlineThreshold) {
                // calculate how many of them are below aimlime
                const belowResultsAmount = getPointsBelowAimlineAmount(
                    aimlineStartPoint,
                    aimlineMiddlePoints,
                    aimlineEndPoint,
                );

                if (belowResultsAmount === measureAimlineThreshold) {
                    // if all of them are below, then measure is off track
                    recommendationStatuses[measure] = RECOMMENDATION_STATUSES[appName].OFF_TRACK;
                } else if (belowResultsAmount === 0) {
                    // if all of them are above, then measure is on track
                    recommendationStatuses[measure] = RECOMMENDATION_STATUSES[appName].ON_TRACK;
                }
            }
        }
    });

    return recommendationStatuses;
};
